<template>
  <nav v-if="appReady" class="desktop-menu">
    <template v-if="!isAuthenticated">
      <div class="unauthenticated-menu">
        <router-link class="desktop-menu__item" :to="$routes.SEARCH">
          {{ $t('menu_items_link_4') }}
        </router-link>

        <router-link class="desktop-menu__item" :to="$routes.HELP">
          {{ $t('menu_items_link_5') }}
        </router-link>

        <router-link class="desktop-menu__item" :to="$routes.LOGIN">
          {{ $t('menu_items_link_8') }}
        </router-link>

        <BaseButton
          :link="$routes.GET_STARTED"
          class="rounded"
          style="margin-left: 20px"
        >
          {{ $t('menu_items_link_9') }}
        </BaseButton>
      </div>
    </template>

    <template v-else>
      <div class="center-menu-container">
        <router-link
          :class="[
            'desktop-menu__item desktop-menu__item--nav',
            { active: activeNavItem === 'swapList' }
          ]"
          :to="$routes.SWAP_LIST"
          active-class=""
          exact-active-class=""
        >
          <span class="desktop-menu__label">
            {{ $t('menu_items_link_1') }}
          </span>
        </router-link>

        <router-link
          :class="[
            'desktop-menu__item desktop-menu__item--nav',
            { active: activeNavItem === 'matches' }
          ]"
          :to="$routes.MATCHES"
          active-class=""
          exact-active-class=""
        >
          <span class="desktop-menu__label">
            {{ $t('menu_items_link_13') }}
            <!-- Matchningsvy v3: something to see there is a still dot,
                 not a count. -->
            <NotificationDot
              v-if="isMatchesV3 && unreadMatchesAndRequests > 0"
              still
              class="onboarding-notification-dot matches-notification-dot"
            />
          </span>
          <BadgeCount
            v-if="!isMatchesV3"
            :notifications="
              notifications && notifications.unreadMatchesAndRequests
            "
            class="inline"
          />
        </router-link>

        <router-link
          :class="[
            'desktop-menu__item desktop-menu__item--nav',
            { active: activeNavItem === 'myProp' }
          ]"
          :to="$routes.MY_PROPOSITIONS"
          active-class=""
          exact-active-class=""
        >
          <span class="desktop-menu__label">
            {{ $t('tab_bar_my_ads') }}
          </span>
        </router-link>
      </div>

      <div class="right-menu-container">
        <div class="premium-and-boost-item-container">
          <router-link
            v-if="
              !primaryPropositionIsPremiumOrFree &&
              $country.isFeatureEnabled('PAYMENT')
            "
            :to="$routes.PAYMENT_PROPOSITION + '/' + primaryPropositionId"
            class="desktop-menu__item"
          >
            <div class="premium-and-boost-item">
              <BaseIcon
                icon-file="premium-icon"
                :width="24"
                :height="24"
                class="icon"
              />
              <div class="premium-and-boost-item-text">
                {{ $t('get_premium') }}
              </div>
            </div>
          </router-link>
        </div>

        <div class="premium-and-boost-item-container">
          <button
            v-if="showBoostButton"
            aria-label="Boost"
            class="desktop-menu__item"
            @click="handleBoostClick"
          >
            <div class="premium-and-boost-item">
              <BoostEndedIcon v-if="showBoostEndedIcon" class="ended-icon" />
              <BaseIcon
                v-else
                icon-file="icon-rocket-purple"
                :width="24"
                :height="24"
                class="icon"
              />
              <div class="premium-and-boost-item-text">
                {{
                  hasBoost || showBoostEndedIcon
                    ? $t('menu_items_your_boost')
                    : $t('menu_items_get_boost')
                }}
              </div>
            </div>
          </button>
        </div>

        <div id="js-notification-toggle" class="notification-center">
          <button
            :aria-label="$t('notifications_title')"
            class="notification-toggle-icon-container"
            @click="handleNotificationCenterClick"
          >
            <BaseIcon
              :icon="notificationCenterIcon"
              :width="40"
              :height="40"
              :class="{ 'is-open': notificationCenterOpen }"
              class="notification-toggle-icon"
            />
            <div class="notifications-count">
              <BadgeCount
                :notifications="unseenNotificationsCount"
                class="inline"
              />
            </div>
          </button>
          <NotificationCenterContainer
            v-if="notificationCenterOpen && !isMobile"
          />
        </div>

        <button
          id="js-popup-toggle"
          :aria-label="$t('tab_bar_more')"
          class="menu-container"
          @click="toggleMenu"
        >
          <BaseIcon
            icon-file="hamburger"
            :width="16"
            :height="16"
            class="hamburger-icon"
          />
          <ProfileAvatar
            class="desktop-avatar"
            :size="30"
            :avatar="userAvatar"
          />
        </button>
      </div>
    </template>
  </nav>
</template>

<script>
import NotificationDot from '../Icons/NotificationDot';
import BadgeCount from '../Icons/BadgeCount';
import ProfileAvatar from '../Icons/ProfileAvatar';
import BoostEndedIcon from '@/components/BoostEndedIcon';
import NotificationCenterContainer from '@/components/NotificationCenter/NotificationCenterContainer';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import events from '@/utils/helpers/events';
import { getActiveNavItem } from '@/utils/activeNavItem';
import { isMatchesV3Enabled } from '@/utils/matchesV3';
import BaseButton from '@/components/BaseButton.vue';
import BaseIcon from '@/components/BaseIcon.vue';

export default {
  name: 'MenuItems',

  components: {
    NotificationDot,
    BadgeCount,
    BoostEndedIcon,
    ProfileAvatar,
    NotificationCenterContainer,
    BaseButton,
    BaseIcon
  },

  props: {
    userAvatar: {
      type: String,
      default: ''
    },
    notifications: {
      type: Object,
      required: true
    },
    isAuthenticated: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  computed: {
    isMatchesV3() {
      return isMatchesV3Enabled(this.$growthbook);
    },

    unreadMatchesAndRequests() {
      return (
        (this.notifications && this.notifications.unreadMatchesAndRequests) || 0
      );
    },

    ...mapGetters({
      isTestEnabled: 'app/isTestEnabled',
      inReboostWindow: 'extraVisibility/inReboostWindow',
      appReady: 'app/ready',
      notificationCenterOpen: 'menu/isNotificationCenterOpen',
      unseenNotificationsCount: 'notificationCenter/unseenNotificationsCount',
      isMobile: 'screenSize/isMobile',
      swapInterestCountsUnseenTotal: 'swapList/swapInterestCountsUnseenTotal',
      showSwapAddedBadge: 'swapList/showSwapAddedBadge',
      primaryProposition: 'myPropositions/primaryProposition',
      primaryPropositionId: 'myPropositions/primaryPropositionId',
      primaryPropositionIsPremiumOrFree:
        'myPropositions/primaryPropositionIsPremiumOrFree',
      primaryPropositionActive: 'myPropositions/primaryPropositionActive'
    }),

    hasBoost() {
      return this.primaryProposition.extraVisibility;
    },

    consumablesEnabled() {
      return this.isTestEnabled('consumables');
    },

    showBoostButton() {
      return (
        this.primaryPropositionActive &&
        this.primaryPropositionIsPremiumOrFree &&
        this.$country.isFeatureEnabled('PAYMENT')
      );
    },

    // Consumables A/B: the ad whose re-boost window the button reads —
    // none for a hidden button, and the flag is read last, so nobody else
    // is evaluated or makes the request. Read even while the ad carries a
    // boost: its extraVisibility (and endDate) stay for a while after the
    // boost ended, so only /api/boost-summary knows it has ended.
    reboostWindowPropositionId() {
      if (!this.showBoostButton) return null;
      if (!this.consumablesEnabled) return null;
      return this.primaryProposition.propositionId || null;
    },

    // For 24 h after the boost ended the button pulses and (see
    // handleBoostClick) reopens the result panel.
    showBoostEndedIcon() {
      return this.consumablesEnabled && this.inReboostWindow;
    },

    activeNavItem() {
      return getActiveNavItem({
        path: this.$route.path,
        query: this.$route.query
      });
    },

    notificationCenterIcon() {
      if (this.notificationCenterOpen) {
        return 'bell-large-blue';
      } else {
        return 'bell-large';
      }
    }
  },

  watch: {
    reboostWindowPropositionId: {
      immediate: true,
      handler(propositionId) {
        if (propositionId) this.loadReboostWindow(propositionId);
      }
    },

    // A boost bought (or dropped) on the ad: the window read before it
    // is stale, so read it again.
    hasBoost(now, before) {
      if (!now === !before || !this.reboostWindowPropositionId) return;
      this.loadReboostWindow(this.reboostWindowPropositionId);
    }
  },

  methods: {
    ...mapActions({
      toggleMenu: 'menu/toggleMenu',
      toggleNotificationCenter: 'menu/toggleNotificationCenter',
      setGlobalModal: 'ui/setGlobalModal',
      loadReboostWindow: 'extraVisibility/loadReboostWindow'
    }),

    ...mapMutations({
      setExtraVisibilityPaymentVisible: 'extraVisibility/setVisible',
      setExtraVisibilityPaymentProposition: 'extraVisibility/setProposition'
    }),

    async handleBoostClick() {
      // Consumables A/B: for 24 h after the boost ended the button reopens
      // the result panel with the re-boost offer; control goes to the
      // purchase as before. A fresh read, so the click and the icon agree,
      // and it comes first: the ad can still carry an ended boost.
      const inReboostWindow =
        this.consumablesEnabled &&
        (await this.loadReboostWindow(this.primaryProposition.propositionId));
      if (inReboostWindow || this.hasBoost) {
        this.setGlobalModal('BoostInfo');
        events.emitEvent(events.eventTypes.CLICK, 'Boost Statistics', {
          src: 'boost-info-menu'
        });
        return;
      }
      this.setExtraVisibilityPaymentProposition(this.primaryProposition);
      this.setExtraVisibilityPaymentVisible(true);
    },

    handleNotificationCenterClick(e) {
      e.stopPropagation();
      this.toggleNotificationCenter();
    }
  }
};
</script>

<style lang="scss" scoped>
.desktop-menu {
  display: none;
  align-items: center;
  display: flex;

  &__item {
    display: flex;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    border-bottom: 2px solid transparent;
    color: var(--Text-Primary);
    font-size: 16px;
    font-weight: 600;
    height: $desktop-menu-height;
    line-height: $desktop-menu-height;
    margin-left: 10px;
    padding: 0 16px;
    position: relative;
    text-decoration: none;
    white-space: nowrap;
    transition: background-color 0.3s ease-in-out;
    cursor: pointer;

    @media (max-width: 1250px) {
      padding: 0 8px;
      font-size: 14px;
    }

    &--nav {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: 0;
      // 16px on each side preserves the original 24px icon + 8px gap footprint.
      padding: 0 16px;
      color: var(--Text-Secondary);
      border-bottom: 2px solid transparent;

      @media (max-width: 1250px) {
        padding: 0;
      }

      &:hover {
        color: var(--text-link, #2276fc);
      }

      &.active {
        color: var(--text-primary, #0b182c);
        border-bottom: 2px solid var(--text-link, #2276fc);
      }
    }
  }

  &__label {
    display: flex;
    align-items: center;
    position: relative;
    // Reset inherited 80px line-height so the label's bounding box hugs the
    // text — otherwise the absolutely-positioned NotificationDot anchors to
    // the 80px-tall label and floats far above the visible text.
    line-height: normal;
  }
}

.onboarding-notification-dot {
  top: -7px;
  right: -14px;
}

.premium-and-boost-item-container {
  @media (max-width: 1250px) {
    margin-right: 2px;
  }
}

.premium-and-boost-item {
  display: flex;
  align-items: center;

  .icon {
    margin-right: 10px;

    @media (max-width: 1250px) {
      margin-right: 0;
    }
  }
}

// The pulsing icon is a 38 px box around a 24 px ring: the negative margins
// give it the regular icon's 24 px slot, so the label does not move.
.ended-icon {
  flex-shrink: 0;
  margin: -7px 3px -7px -7px;

  @media (max-width: 1250px) {
    margin-right: -7px;
  }
}

.premium-and-boost-item-text {
  @media (max-width: 1250px) {
    display: none;
  }
}

.notification-center {
  position: relative;
}

.notification-toggle-icon-container {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  margin-left: 15px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #e8e8e8;
  }
}

.notification-toggle-icon {
  transition: transform 0.1s ease-in-out;

  &.is-open {
    transform: rotate(20deg);
  }
}

.menu-container {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  padding: 5px 5px 5px 12px;
  background: transparent;
  cursor: pointer;
  margin: 0;
  text-align: inherit;
  background-color: #fff;
  border: 1px solid rgba(28, 45, 65, 0.103);
  border-radius: 21px;
  transition: box-shadow 0.2s ease;
  margin-left: 22px;

  &:focus,
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
  }
}

.unauthenticated-menu {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: flex-end;
  padding: 0 $navbar-padding;
}

.center-menu-container {
  display: none;

  @media ($desktop) {
    display: flex;
    align-items: center;
    gap: 32px;
    margin-right: 20px;
  }

  @media (min-width: 1023px) {
    justify-content: center;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    pointer-events: auto;
    max-width: 50%;
    margin-right: 0;
  }
}

.right-menu-container {
  display: none;
  padding-right: 30px;

  @media (min-width: 1023px) {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 1022px) {
    padding-right: 0;
  }

  @media ($desktop) {
    display: flex;
    align-items: center;
    margin-left: auto;
  }
}

.desktop-avatar {
  border-radius: 50%;
  margin-left: 12px;
}

.active {
  border-bottom: 2px solid var(--Primary-Color);
}

.swap-added-icon {
  visibility: hidden;
  position: absolute;
  top: 15px;
  right: 0;
  width: 12px;
  height: 12px;
  background-color: #2ec973;
  border-radius: 50%;
  font-size: 8px;
  color: #2ec973;
  display: flex;
  place-items: center;
}

.plus-one-enter-active {
  visibility: visible;
  animation-name: plus-one;
  animation-duration: 1.5s;
  animation-timing-function: ease-in-out;
}

.notifications-count {
  position: absolute;
  right: -4px;
  top: -4px;
}

@keyframes plus-one {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(4);
    border: none;
    color: #fff;
    padding-left: 2px;
  }
  100% {
    transform: scale(1);
  }
}
</style>
